import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useQuestionsFeed, useSession, useUpdateSession, useCompleteSession, useSubmitAttempt, useCreateShare } from '../lib/api';
import { scrollAppToTop } from '../lib/scroll';

const ADVANCE_DELAY_CORRECT = 600;
const ADVANCE_DELAY_WRONG = 1300;

/**
 * Quick Memory mode: one-line questions with 4 short tappable answers.
 * Tap, see right or wrong instantly, move on. No explanations; the goal is
 * speed memorization, not analysis.
 */
export function QuickMemoryPage() {
  const { sectionId } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session');
  const navigate = useNavigate();

  const { data: feed } = useQuestionsFeed({ class_section_id: sectionId });
  const { data: sessionData } = useSession(sessionId);
  const updateSession = useUpdateSession();
  const completeSession = useCompleteSession();
  const submitAttempt = useSubmitAttempt();

  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState<number | null>(null);
  const [correctCount, setCorrectCount] = useState(0);
  const [answeredCount, setAnsweredCount] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [sessionComplete, setSessionComplete] = useState(false);
  const [sessionQuestionIds, setSessionQuestionIds] = useState<string[] | null>(null);
  const [shareStatus, setShareStatus] = useState<'idle' | 'sharing' | 'copied'>('idle');
  const createShare = useCreateShare();
  const hydratedRef = useRef<string | null>(null);
  const xpRef = useRef(0);
  const startRef = useRef(Date.now());
  const advanceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const quickQuestions = useMemo(
    () => (feed?.questions ?? []).filter((q: any) => q.type === 'quick_mcq'),
    [feed?.questions],
  );
  const answeredSet = useMemo(
    () => new Set(feed?.answeredQuestionIds ?? []),
    [feed?.answeredQuestionIds],
  );

  // Hydrate session state from server
  useEffect(() => {
    if (!sessionData?.session) return;
    const s = sessionData.session;
    if (hydratedRef.current === s.id) return;
    hydratedRef.current = s.id;
    setSessionQuestionIds(s.questionIds);
    setIdx(s.currentIndex);
    setCorrectCount(s.correctCount);
    setAnsweredCount(s.questionsAnswered);
    xpRef.current = (s as any).xpEarned ?? 0;
  }, [sessionData]);

  const orderedQuestions = useMemo(() => {
    if (!sessionQuestionIds) return quickQuestions;
    const byId = new Map(quickQuestions.map((q: any) => [q.id, q]));
    return sessionQuestionIds.map(id => byId.get(id)).filter(Boolean) as any[];
  }, [quickQuestions, sessionQuestionIds]);

  const current = orderedQuestions[idx];
  const payload = current?.payload as any;
  const total = orderedQuestions.length;

  // Reset per-question state
  useEffect(() => {
    setPicked(null);
    startRef.current = Date.now();
    scrollAppToTop('smooth');
    return () => { if (advanceRef.current) clearTimeout(advanceRef.current); };
  }, [idx]);

  const handleShare = useCallback(async () => {
    if (!current || shareStatus === 'sharing') return;
    setShareStatus('sharing');
    try {
      const res = await createShare.mutateAsync({ question_id: current.id, share_type: 'social' });
      const url = `https://app.squishyschool.com/q/${res.share_id}`;
      if (navigator.share) {
        await navigator.share({ title: 'Can you get this right in 5 seconds?', url });
      } else {
        await navigator.clipboard.writeText(url);
      }
      setShareStatus('copied');
      setTimeout(() => setShareStatus('idle'), 2000);
    } catch {
      setShareStatus('idle');
    }
  }, [current, shareStatus, createShare]);

  const advance = useCallback((newCorrect: number, newAnswered: number) => {
    const nextIdx = idx + 1;
    if (nextIdx >= total) {
      setSessionComplete(true);
      if (sessionId) {
        updateSession.mutate({
          id: sessionId,
          currentIndex: idx,
          correctCount: newCorrect,
          questionsAnswered: newAnswered,
          xpEarned: xpRef.current,
        });
        completeSession.mutate(sessionId);
      }
    } else {
      setIdx(nextIdx);
      if (sessionId) {
        updateSession.mutate({
          id: sessionId,
          currentIndex: nextIdx,
          correctCount: newCorrect,
          questionsAnswered: newAnswered,
          xpEarned: xpRef.current,
        });
      }
    }
  }, [idx, total, sessionId, updateSession, completeSession]);

  const handlePick = useCallback((optionIdx: number) => {
    if (picked !== null || !current) return;
    const isCorrect = optionIdx === payload?.correct;
    setPicked(optionIdx);

    const newCorrect = correctCount + (isCorrect ? 1 : 0);
    const newAnswered = answeredCount + 1;
    setCorrectCount(newCorrect);
    setAnsweredCount(newAnswered);
    const newStreak = isCorrect ? streak + 1 : 0;
    setStreak(newStreak);
    if (newStreak > bestStreak) setBestStreak(newStreak);

    submitAttempt.mutate({
      questionId: current.id,
      correct: isCorrect,
      firstTry: !answeredSet.has(current.id),
      selectedAnswers: [optionIdx],
      timeMs: Date.now() - startRef.current,
      studySessionId: sessionId,
    }, {
      onSuccess: (result) => { xpRef.current += result.xpEarned + (result.speedBonus ?? 0); },
    });

    advanceRef.current = setTimeout(
      () => advance(newCorrect, newAnswered),
      isCorrect ? ADVANCE_DELAY_CORRECT : ADVANCE_DELAY_WRONG,
    );
  }, [picked, current, payload, correctCount, answeredCount, streak, bestStreak, answeredSet, sessionId, submitAttempt, advance]);

  if (sessionComplete) {
    const accuracy = answeredCount > 0 ? Math.round((correctCount / answeredCount) * 100) : 0;
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-6 max-w-sm w-full text-center space-y-4">
          <div className="text-5xl">&#x26A1;</div>
          <h1 className="text-xl font-bold text-squishy-dark">Quick Study Complete!</h1>
          <div className="flex justify-center gap-6 text-sm">
            <div>
              <div className="text-2xl font-bold text-green-600">{correctCount}</div>
              <div className="text-slate-500">Right</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-red-500">{answeredCount - correctCount}</div>
              <div className="text-slate-500">Wrong</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-amber-500">{bestStreak}</div>
              <div className="text-slate-500">Best streak</div>
            </div>
          </div>
          <div className="text-3xl font-bold text-amber-500">{accuracy}%</div>
          {xpRef.current > 0 && (
            <p className="text-xs font-semibold text-emerald-600">+{xpRef.current} XP earned</p>
          )}
          <button
            onClick={() => navigate(`/class/${sectionId}`)}
            className="w-full bg-amber-500 text-white py-3 rounded-xl font-semibold text-sm active:scale-[0.97] transition-transform"
          >
            Back to Class
          </button>
        </div>
      </div>
    );
  }

  if (!current) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-slate-400 text-sm">Loading quick questions...</div>
      </div>
    );
  }

  const options: string[] = payload?.options ?? [];
  const correctIdx: number = payload?.correct;

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white/90 backdrop-blur-sm border-b px-4 py-2.5">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <button
            onClick={() => navigate(`/class/${sectionId}`)}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold text-squishy-dark">Quick Study</div>
            <div className="text-[10px] text-slate-400">{idx + 1} of {total}</div>
          </div>
          <div className="flex items-center gap-2 text-[10px]">
            {streak >= 3 && (
              <span className="text-amber-500 font-bold animate-pulse">&#x1F525; {streak}</span>
            )}
            <span className="text-green-600 font-semibold">{correctCount} &#x2713;</span>
            <span className="text-red-500 font-semibold">{answeredCount - correctCount} &#x2717;</span>
            <button
              onClick={handleShare}
              disabled={shareStatus === 'sharing'}
              className="ml-1 p-1.5 rounded-full text-slate-400 hover:text-amber-500 hover:bg-amber-50 transition-colors"
              title="Share this question"
            >
              {shareStatus === 'copied' ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
              )}
            </button>
          </div>
        </div>
        {/* Progress bar */}
        <div className="max-w-lg mx-auto mt-1.5">
          <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-amber-400 rounded-full transition-all duration-300"
              style={{ width: `${((idx + 1) / total) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Question area */}
      <div className="max-w-lg mx-auto px-4 pt-8">
        {payload?.topic && (
          <div className="text-center mb-3">
            <span className="inline-block text-[10px] font-semibold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full uppercase tracking-wider">
              {payload.topic}
            </span>
          </div>
        )}
        <h1 className="text-xl font-bold text-squishy-dark text-center leading-snug min-h-[3.5rem] flex items-center justify-center">
          {payload?.question}
        </h1>

        {/* Options: 2x2 grid of short tappable answers */}
        <div className="grid grid-cols-2 gap-3 mt-8">
          {options.map((opt, i) => {
            let style = 'border-slate-200 bg-white text-squishy-dark active:scale-[0.96]';
            if (picked !== null) {
              if (i === correctIdx) style = 'border-green-500 bg-green-50 text-green-700';
              else if (i === picked) style = 'border-red-400 bg-red-50 text-red-600 animate-shake';
              else style = 'border-slate-100 bg-slate-50 text-slate-300';
            }
            return (
              <button
                key={i}
                onClick={() => handlePick(i)}
                disabled={picked !== null}
                className={`min-h-[72px] px-3 py-4 rounded-2xl border-2 font-semibold text-base leading-tight transition-all ${style}`}
              >
                {opt}
              </button>
            );
          })}
        </div>

        {/* Feedback flash */}
        <div className="h-10 mt-5 text-center">
          {picked !== null && picked === correctIdx && (
            <span className="text-green-600 font-bold text-sm">Correct!</span>
          )}
          {picked !== null && picked !== correctIdx && (
            <span className="text-red-500 font-bold text-sm">
              Answer: {options[correctIdx]}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
